/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './chronic-condition.reducer';

export const ChronicCondition = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const chronicConditionList = useAppSelector(state => state.chronicCondition.entities);
  const loading = useAppSelector(state => state.chronicCondition.loading);
  const totalItems = useAppSelector(state => state.chronicCondition.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="chronic-condition-heading" data-cy="ChronicConditionHeading">
        <Translate contentKey="tinyMedicRestApp.chronicCondition.home.title">Chronic Conditions</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.chronicCondition.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/chronic-condition/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.chronicCondition.home.createLabel">Create new Chronic Condition</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.chronicCondition.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {chronicConditionList && chronicConditionList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.chronicCondition.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('category')}>
                  <Translate contentKey="tinyMedicRestApp.chronicCondition.category">Category</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('category')} />
                </th>
                <th className="hand" onClick={sort('subCategory')}>
                  <Translate contentKey="tinyMedicRestApp.chronicCondition.subCategory">Sub Category</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('subCategory')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.chronicCondition.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.chronicCondition.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('lastupdated')}>
                  <Translate contentKey="tinyMedicRestApp.chronicCondition.lastupdated">Lastupdated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastupdated')} />
                </th>
                <th className="hand" onClick={sort('timestampentry')}>
                  <Translate contentKey="tinyMedicRestApp.chronicCondition.timestampentry">Timestampentry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampentry')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.chronicCondition.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th className="hand" onClick={sort('snomedConceptId')}>
                  <Translate contentKey="tinyMedicRestApp.chronicCondition.snomedConceptId">Snomed Concept Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('snomedConceptId')} />
                </th>
                <th className="hand" onClick={sort('snomedConceptText')}>
                  <Translate contentKey="tinyMedicRestApp.chronicCondition.snomedConceptText">Snomed Concept Text</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('snomedConceptText')} />
                </th>
                <th className="hand" onClick={sort('topographyStart')}>
                  <Translate contentKey="tinyMedicRestApp.chronicCondition.topographyStart">Topography Start</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('topographyStart')} />
                </th>
                <th className="hand" onClick={sort('topographyEnd')}>
                  <Translate contentKey="tinyMedicRestApp.chronicCondition.topographyEnd">Topography End</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('topographyEnd')} />
                </th>
                <th className="hand" onClick={sort('std')}>
                  <Translate contentKey="tinyMedicRestApp.chronicCondition.std">Std</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('std')} />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.chronicCondition.diagnosis">Diagnosis</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.chronicCondition.topography">Topography</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.chronicCondition.morphologyGroup">Morphology Group</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.chronicCondition.morphology">Morphology</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.chronicCondition.behaviour">Behaviour</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {chronicConditionList.map((chronicCondition, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/chronic-condition/${chronicCondition.id}`} color="link" size="sm">
                      {chronicCondition.id}
                    </Button>
                  </td>
                  <td>{chronicCondition.category}</td>
                  <td>{chronicCondition.subCategory}</td>
                  <td>{chronicCondition.active ? 'true' : 'false'}</td>
                  <td>{chronicCondition.microSec}</td>
                  <td>{chronicCondition.lastupdated}</td>
                  <td>{chronicCondition.timestampentry}</td>
                  <td>{chronicCondition.cursor}</td>
                  <td>{chronicCondition.snomedConceptId}</td>
                  <td>{chronicCondition.snomedConceptText}</td>
                  <td>{chronicCondition.topographyStart}</td>
                  <td>{chronicCondition.topographyEnd}</td>
                  <td>{chronicCondition.std ? 'true' : 'false'}</td>
                  <td>
                    {chronicCondition.diagnosis ? (
                      <Link to={`/icd-10-diagnosis/${chronicCondition.diagnosis.id}`}>{chronicCondition.diagnosis.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {chronicCondition.topography ? (
                      <Link to={`/clinical-dictionary/${chronicCondition.topography.id}`}>{chronicCondition.topography.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {chronicCondition.morphologyGroup ? (
                      <Link to={`/clinical-dictionary/${chronicCondition.morphologyGroup.id}`}>{chronicCondition.morphologyGroup.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {chronicCondition.morphology ? (
                      <Link to={`/clinical-dictionary/${chronicCondition.morphology.id}`}>{chronicCondition.morphology.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {chronicCondition.behaviour ? (
                      <Link to={`/clinical-dictionary/${chronicCondition.behaviour.id}`}>{chronicCondition.behaviour.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/chronic-condition/${chronicCondition.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/chronic-condition/${chronicCondition.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/chronic-condition/${chronicCondition.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.chronicCondition.home.notFound">No Chronic Conditions found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={chronicConditionList && chronicConditionList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default ChronicCondition;
